import { Controller } from "@hotwired/stimulus"

// 비밀번호 검증 컨트롤러
// - 입력 중: 실시간으로 비밀번호 강도 표시 (progress bar)
// - 포커스 잃을 때: 형식 검증 결과 표시
// - registration-form 컨트롤러에 상태 변경 알림
// 참고: Next.js /src/app/user/register/user-info/page.tsx
export default class extends Controller {
  static targets = [
    "input",
    "lengthRequirement",
    "charsRequirement",
    "strengthContainer",
    "strengthBar1",
    "strengthBar2",
    "strengthBar3",
    "strengthText",
    "validMessage",
    "invalidMessage"
  ]
  static outlets = ["registration-form"]

  connect() {
    this.hideValidationMessages()
    this.strengthContainerTarget.classList.add("hidden")
    this._isValid = false
  }

  // 외부에서 비밀번호 유효 상태 확인용
  get isPasswordValid() {
    return this._isValid
  }

  // registration-form 컨트롤러에 상태 변경 알림
  notifyFormController() {
    if (this.hasRegistrationFormOutlet) {
      this.registrationFormOutlet.passwordValidationChanged()
    }
  }

  // 입력 중 실시간 검증 (input 이벤트)
  onInput() {
    const password = this.inputTarget.value

    if (!password) {
      this._isValid = false
      this.resetAll()
      this.notifyFormController()
      return
    }

    // 비밀번호 검증 수행
    const validation = this.validatePassword(password)

    // 유효 상태 업데이트
    this._isValid = validation.isValid

    // 요구사항 체크 업데이트
    this.updateRequirements(validation)

    // 강도 계산 및 표시
    this.updateStrengthBar(validation.strength, validation.strengthLevel)

    // 폼 컨트롤러에 알림
    this.notifyFormController()
  }

  // 포커스 잃을 때 최종 검증 (blur 이벤트)
  onBlur() {
    const password = this.inputTarget.value

    if (!password) {
      this._isValid = false
      this.resetAll()
      this.notifyFormController()
      return
    }

    const validation = this.validatePassword(password)
    this._isValid = validation.isValid

    if (validation.isValid) {
      this.showValid()
    } else {
      this.showInvalid()
    }

    this.notifyFormController()
  }

  // 비밀번호 검증 (Next.js passwordValidation useMemo와 동일)
  // 규칙: 8자 이상, 영문자 + 숫자 + 특수문자(@$!%*?&) 포함
  validatePassword(password) {
    const hasLength = password.length >= 8
    const hasLetter = /[a-zA-Z]/.test(password)
    const hasNumber = /\d/.test(password)
    const hasSpecial = /[@$!%*?&]/.test(password)

    // 전체 형식 검증: 영문, 숫자, 허용된 특수문자만 포함
    const hasValidFormat = /^(?=.*[a-zA-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]+$/.test(password)
    const hasAllChars = hasLetter && hasNumber && hasSpecial

    // 강도 계산 (Next.js와 동일한 로직)
    let strength = 0
    if (/[a-z]/.test(password) && /[A-Z]/.test(password)) strength++  // 대소문자 모두 포함
    if (hasNumber) strength++   // 숫자 포함
    if (hasSpecial) strength++  // 특수문자 포함
    if (hasLength) strength++   // 8자 이상

    // 강도 레벨 결정 (Next.js와 동일)
    let strengthLevel = "weak"
    if (strength >= 3) strengthLevel = "strong"
    else if (strength >= 2) strengthLevel = "fair"

    return {
      hasLength,
      hasAllChars,
      hasValidFormat,
      strength,
      strengthLevel,
      isValid: hasLength && hasValidFormat
    }
  }

  updateRequirements(validation) {
    // 길이 요구사항 업데이트
    if (validation.hasLength) {
      this.lengthRequirementTarget.classList.remove("text-gray-500")
      this.lengthRequirementTarget.classList.add("text-success-700")
    } else {
      this.lengthRequirementTarget.classList.remove("text-success-700")
      this.lengthRequirementTarget.classList.add("text-gray-500")
    }

    // 문자 요구사항 업데이트 (영문/숫자/특수문자 모두 포함)
    if (validation.hasAllChars) {
      this.charsRequirementTarget.classList.remove("text-gray-500")
      this.charsRequirementTarget.classList.add("text-success-700")
    } else {
      this.charsRequirementTarget.classList.remove("text-success-700")
      this.charsRequirementTarget.classList.add("text-gray-500")
    }
  }

  updateStrengthBar(strength, strengthLevel) {
    this.strengthContainerTarget.classList.remove("hidden")

    // 색상 매핑 (Next.js strengthColors와 동일)
    const colorClasses = {
      weak: "bg-danger-500",
      fair: "bg-orange-500",
      strong: "bg-success-500"
    }

    // 텍스트 색상 매핑 (Next.js strengthTextColors와 동일)
    const textColorClasses = {
      weak: "text-danger-700",
      fair: "text-orange-700",
      strong: "text-success-700"
    }

    // 레이블 (Next.js strengthLabels와 동일)
    const labels = {
      weak: "약함",
      fair: "보통",
      strong: "강함"
    }

    // 강도 바 업데이트 (Next.js 렌더링 로직과 동일: i < strength)
    const bars = [this.strengthBar1Target, this.strengthBar2Target, this.strengthBar3Target]
    bars.forEach((bar, index) => {
      // 기존 색상 클래스 제거
      bar.classList.remove("bg-danger-500", "bg-orange-500", "bg-success-500", "bg-gray-200")

      // index < strength 이면 현재 레벨의 색상, 아니면 회색
      if (index < strength) {
        bar.classList.add(colorClasses[strengthLevel])
      } else {
        bar.classList.add("bg-gray-200")
      }
    })

    // 강도 텍스트 업데이트
    this.strengthTextTarget.textContent = `(${labels[strengthLevel]})`
    this.strengthTextTarget.classList.remove("text-danger-700", "text-orange-700", "text-success-700")
    this.strengthTextTarget.classList.add(textColorClasses[strengthLevel])
  }

  hideValidationMessages() {
    this.validMessageTarget.classList.add("hidden")
    this.invalidMessageTarget.classList.add("hidden")
  }

  showValid() {
    this.hideValidationMessages()
    this.validMessageTarget.classList.remove("hidden")
    this.inputTarget.classList.remove("border-danger-500")
    this.inputTarget.classList.add("border-success-500")
  }

  showInvalid() {
    this.hideValidationMessages()
    this.invalidMessageTarget.classList.remove("hidden")
    this.inputTarget.classList.remove("border-success-500")
    this.inputTarget.classList.add("border-danger-500")
  }

  resetAll() {
    this.hideValidationMessages()
    this.strengthContainerTarget.classList.add("hidden")
    this.inputTarget.classList.remove("border-success-500", "border-danger-500")

    // 요구사항 초기화
    this.lengthRequirementTarget.classList.remove("text-success-700")
    this.lengthRequirementTarget.classList.add("text-gray-500")
    this.charsRequirementTarget.classList.remove("text-success-700")
    this.charsRequirementTarget.classList.add("text-gray-500")
  }
}
